import {
  Currency,
  type BookingProposalRecordList,
  type MoneyRecord,
  type SalesItemWithQuantityRecord
} from '@navio/nsdk';
import { getCarDisplayName, getCarLicensePlateText } from 'noddi-util';

/** The fields a compact proposal row reads — a full `BookingProposalRecordList` satisfies it. */
export type ProposalRowSource = Pick<BookingProposalRecordList, 'bookingProposalItems' | 'carManager'>;

export type ProposalRowSummary = {
  carName: string;
  licensePlateNumber: string;
  /** Vehicles beyond the first one, for proposals that cover a whole set of cars. */
  extraVehicleCount: number;
  /** Product names on the proposal, deduplicated in API order. */
  productNames: string[];
  /**
   * The car's driver — reported by the API as the car manager, the member the
   * proposal's driver picker assigns. Null on private customers' proposals.
   */
  driverName: string | null;
  total: MoneyRecord;
};

/**
 * List price of one proposal line. An ad-hoc price is the recall's own negotiated
 * amount and always wins over the product's catalog unit price.
 */
const getLineGross = ({ priceAdHoc, quantity, salesItem }: SalesItemWithQuantityRecord): MoneyRecord => {
  const unitPrice = priceAdHoc?.priceGross ?? salesItem.price.priceGross;
  return { amount: unitPrice.amount * quantity, currency: unitPrice.currency };
};

const sumGross = (lines: MoneyRecord[]): MoneyRecord => ({
  amount: lines.reduce((total, line) => total + line.amount, 0),
  currency: lines[0]?.currency ?? Currency.NOK
});

/**
 * Gross total of a proposal's own lines. Cart-level discounts (coupons, membership
 * benefits) land on the confirmation page, so this reads as the list price of what
 * the recall proposes — never as the final payable amount.
 */
export const getProposalGrossTotal = (proposal: ProposalRowSource): MoneyRecord =>
  sumGross(proposal.bookingProposalItems.flatMap((item) => item.salesItemsWithQuantity.map(getLineGross)));

export const getProposalRowSummary = (proposal: ProposalRowSource): ProposalRowSummary => {
  const [firstItem, ...restItems] = proposal.bookingProposalItems;
  const productNames = [
    ...new Set(
      proposal.bookingProposalItems.flatMap((item) =>
        item.salesItemsWithQuantity.map(({ salesItem }) => salesItem.name)
      )
    )
  ];

  return {
    carName: firstItem ? getCarDisplayName({ car: firstItem.car, showYear: false, showLicensePlate: false }) : '',
    licensePlateNumber: firstItem ? getCarLicensePlateText(firstItem.car) : '',
    extraVehicleCount: restItems.length,
    productNames,
    driverName: proposal.carManager?.name ?? null,
    total: getProposalGrossTotal(proposal)
  };
};
